import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import {
  CHANGE_REQUEST_REVIEW_STATUS_FILTERS,
  FORM_SUBMISSION_STATUSES,
  WORK_ASSIGNMENT_ORIGINS,
  WORK_ASSIGNMENT_STATUSES,
  type ChangeRequestReviewStatusFilter,
  type FormSubmissionStatus,
  type WorkAssignmentOrigin,
  type WorkAssignmentStatus,
} from "@njbsoft/api-client";
import { useChangeRequestCounts } from "@/hooks/use-change-requests";
import { useNeedsReviewCount } from "@/hooks/use-needs-review-count";
import { needsReviewCountBadge } from "@/components/form-submissions/needs-review-count-badge";
import { usePermissions } from "@/hooks/use-permissions";
import { PageHeader, PageSkeleton, Tabs, type TabDef } from "@/components/ui";
import { changeRequestCountBadge } from "@/components/change-requests/change-request-count-badge";
import { AssignmentsTab } from "@/components/test-activity/assignments-tab";
import { ChangeRequestsTab } from "@/components/test-activity/change-requests-tab";
import { SubmissionsTab } from "@/components/test-activity/submissions-tab";
import { ViolationsTab } from "@/components/test-activity/violations-tab";

type FormTargetKind = components["schemas"]["FormTargetKind"];

type TestActivityTab = "assignments" | "submissions" | "change-requests" | "violations";

const ALLOWED_TABS: TestActivityTab[] = [
  "assignments",
  "submissions",
  "change-requests",
  "violations",
];

// The union of `tab` and the panels' URL-seeded filters, every field
// optional. The panels read these through `getRouteApi` and seed their own
// initial state from them; nothing writes them back, so only a deep link ever
// sets one. Assignments' axes: `origin=follow_up&open=true` narrows to open
// follow-up work; `status=completed` with `completed_from`/`completed_to`
// (optionally `target_kind`) narrows to a completed window. Submissions' axes:
// `target_kind` + one of `pending_review` / `unresolved` / `late`, the
// `status=needs_review` review-queue drill the dashboard card lands, plus the
// device-detail Testing tab's `device_installation_id` prefilter. `status` is
// the ONE axis two panels share: it parses against both vocabularies, and
// each panel seeds from it taking only its own enum's members.
// `target_kind` is the one field both declare, so it is parsed once. Change
// requests' axes: `cr_status` (its own member, since `status` is shared with
// the Assignments and Submissions panels), `facility_id`, and the same
// `device_installation_id`, which there names the requests' target.
interface TestActivitySearch {
  tab?: TestActivityTab;
  status?: WorkAssignmentStatus | FormSubmissionStatus;
  cr_status?: ChangeRequestReviewStatusFilter;
  facility_id?: string;
  origin?: WorkAssignmentOrigin;
  open?: boolean;
  completed_from?: string;
  completed_to?: string;
  late?: boolean;
  unresolved?: boolean;
  pending_review?: boolean;
  device_installation_id?: string;
  target_kind?: FormTargetKind;
}

const TARGET_KINDS: FormTargetKind[] = ["device_test", "premise_inspection", "survey", "incident"];

function parseTab(value: unknown): TestActivityTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

function parseFromSet<T extends string>(v: unknown, set: T[]): T | undefined {
  return typeof v === "string" && (set as string[]).includes(v) ? (v as T) : undefined;
}

function parseTargetKind(v: unknown): FormTargetKind | undefined {
  // The widening lets `includes` accept an arbitrary string (its narrow
  // signature would reject the very value under test); the membership it
  // returns is what proves the narrowing below.
  return typeof v === "string" && (TARGET_KINDS as string[]).includes(v)
    ? (v as FormTargetKind)
    : undefined;
}

function parseBool(v: unknown): boolean | undefined {
  return v === true || v === "true" ? true : undefined;
}

export const Route = createFileRoute("/_authenticated/test-activity/")({
  component: TestActivityPage,
  validateSearch: (s: Record<string, unknown>): TestActivitySearch => ({
    tab: parseTab(s.tab),
    status: parseFromSet(s.status, [...WORK_ASSIGNMENT_STATUSES, ...FORM_SUBMISSION_STATUSES]),
    cr_status: parseFromSet(s.cr_status, [...CHANGE_REQUEST_REVIEW_STATUS_FILTERS]),
    facility_id: typeof s.facility_id === "string" ? s.facility_id : undefined,
    origin: parseFromSet(s.origin, [...WORK_ASSIGNMENT_ORIGINS]),
    open: parseBool(s.open),
    completed_from: typeof s.completed_from === "string" ? s.completed_from : undefined,
    completed_to: typeof s.completed_to === "string" ? s.completed_to : undefined,
    late: parseBool(s.late),
    unresolved: parseBool(s.unresolved),
    pending_review: parseBool(s.pending_review),
    device_installation_id:
      typeof s.device_installation_id === "string" ? s.device_installation_id : undefined,
    target_kind: parseTargetKind(s.target_kind),
  }),
});

/** A tab plus the permission entity its panel reads. */
interface TestActivityTabDef extends TabDef<TestActivityTab> {
  entity: string;
}

function TestActivityPage() {
  const { t } = useTranslation("nav");
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const { data: counts } = useChangeRequestCounts();
  const badge = changeRequestCountBadge(counts, t);
  const { data: needsReviewCount } = useNeedsReviewCount();
  const submissionsBadge = needsReviewCountBadge(needsReviewCount, t);

  // The only place the tab order and the entity each tab gates on live.
  const tabs: TestActivityTabDef[] = [
    {
      value: "assignments",
      label: t("testActivity.assignments", { ns: "nav" }),
      entity: "work_assignments",
    },
    {
      value: "submissions",
      label: t("testActivity.submissions", { ns: "nav" }),
      entity: "form_submissions",
      count: submissionsBadge?.count,
      countTone: submissionsBadge?.tone,
      countLabel: submissionsBadge?.sr,
    },
    {
      value: "change-requests",
      label: t("testActivity.changeRequests", { ns: "nav" }),
      entity: "change_requests",
      count: badge?.count,
      countTone: badge?.tone,
      countLabel: badge?.sr,
    },
    {
      value: "violations",
      label: t("testActivity.violations", { ns: "nav" }),
      entity: "violations",
    },
  ];

  // `usePermissions()` reads deny-all until its envelope resolves, so every
  // decision below has to wait for a real answer — filtering earlier renders a
  // tab bar that flickers from four tabs to one, and bouncing earlier throws
  // every hard navigation (bookmark, deep link, reload) at the home route.
  if (permissionsLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  // Tabs are FILTERED by read, never disabled: a member who cannot read a
  // panel has no business seeing that it exists.
  const visibleTabs = tabs.filter((def) => can(def.entity, "read"));
  const firstVisible = visibleTabs[0];

  // Nothing on this page is readable at all.
  if (!firstVisible) {
    void navigate({ to: "/" });
    return null;
  }

  const activeTab = visibleTabs.find((def) => def.value === tab)?.value ?? firstVisible.value;

  const setActiveTab = (next: TestActivityTab) => {
    void navigate({
      to: "/test-activity",
      // The first VISIBLE tab is the resolved default, so it clears the param
      // instead of stacking a history entry for every tab switch.
      search: next === firstVisible.value ? {} : { tab: next },
      replace: true,
    });
  };

  // A `?tab=` naming a tab this caller cannot see: render the first visible
  // panel and correct the URL. The `tab !== activeTab` guard is what stops the
  // render-body navigate from looping once the param is cleared.
  if (tab !== undefined && tab !== activeTab) {
    void navigate({ to: "/test-activity", search: {}, replace: true });
  }

  return (
    <div className="p-8">
      <div className="space-y-4">
        <PageHeader title={t("testActivity.title", { ns: "nav" })} />

        <div>
          <Tabs
            tabs={visibleTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            ariaLabel={t("testActivity.title", { ns: "nav" })}
          />

          <div
            className="mt-6"
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            {activeTab === "assignments" && <AssignmentsTab />}
            {activeTab === "submissions" && <SubmissionsTab />}
            {activeTab === "change-requests" && <ChangeRequestsTab />}
            {activeTab === "violations" && <ViolationsTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
